// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { HistoryType } from '@shared/utils/History/HistoryType';
import { MathUtils } from '@shared/utils/MathUtils';
import { QuickTableEditingInfo } from '../elements/QuickTable/QuickTableMisc';
import { BaseItem, ColumnData } from './BaseItem';
import { ColumnParams } from './ColumnParams';
import { QuickTableComparingType } from '@shared/utils/QuickTableMisc/QuickTableComparingType';
import { AssetInfoData } from '@shared/commons/cache/AssetInfoData';
import { DynProperty } from '@shared/commons/DynProperty';
import { DataCache } from '@shared/commons/DataCache';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { StringUtils } from '@shared/utils/StringUtils';
import { TableTotalsVisibility } from './TableTotalsVisibility';
import { QTAlignment } from '../elements/QuickTable/QuickTableColumn';
export class AssetsItem extends BaseItem {
    constructor(account, asset, sessionSettings) {
        super(sessionSettings);
        this.userGroupName = '';
        this.Removed = false;
        this.Instrument = null;
        this.SellExchanges = null;
        this.listeners = [];
        this.InfoData = new AssetInfoData();
        this._sellExchange = null;
        this.Account = account;
        this.Asset = asset;
        this.itemId = (AssetsItem._staticId++).toString();
        if (!isNullOrUndefined(this.Account)) {
            this.Account.OnUpdateInfoData.Subscribe(this.Account_OnUpdateInfoData, this);
        }
        this.UpdatInfoData();
        if (!isNullOrUndefined(this.Asset)) {
            this.initializeInstrumentData();
        }
        this.QuickTableEditingInfoMap[AssetsItem.COL_SELL_ASSET_BUTTON] = (function () {
            return new QuickTableEditingInfo(DynProperty.SELL_ASSET_BUTTON);
        })();
    }
    initializeInstrumentData() {
        void this.Asset.GetInstrumentAsync().then(function (instrument) {
            this.Instrument = instrument;
            this.SubscribeInstrument(this.Instrument);
            this.Asset.UpdateSellExchanges().then(function (SellExchanges) {
                this.SellExchanges = SellExchanges;
                this._sellExchange = SellExchanges[0] || '';
                this.fireUpdate();
            }.bind(this));
        }.bind(this));
    }
    Account_OnUpdateInfoData(asset) {
        if (this.Asset !== asset) {
            return;
        }
        this.UpdatInfoData();
        this.fireUpdate();
    }
    UpdatInfoData() {
        if (!isNullOrUndefined(this.Account)) {
            this.InfoData = this.Account.GetAssetDataInfoByName(this.Asset.Name);
        }
    }
    LastPrice() {
        return this.Instrument !== null ? this.Instrument.Level1.GetLastPrice(this.Account) : NaN;
    }
    GetCurrentAccount() {
        return this.Account;
    }
    newQuote(message) {
        if (message === null) {
            return;
        }
        if (message.Type === HistoryType.QUOTE_LEVEL1 ||
            message.Type === HistoryType.QUOTE_TRADES ||
            message.Type === HistoryType.QUOTE_INSTRUMENT_DAY_BAR) {
            this.fireUpdate();
        }
    }
    fireUpdate() {
        if (this.Removed) {
            return;
        }
        this.isNeedFireUpdate = true;
        // for (let i = 0; i < this.listeners.length; i++)
        //     this.listeners[i].updateItem(this);
    }
    GetSellExchange() {
        // if (this._sellExchange)
        //     this._sellExchange = this.Asset.SellExchanges[0] || "";
        return this._sellExchange;
    }
    SetSellExchange(value) {
        this._sellExchange = value;
    }
    Dispose() {
        this.Removed = true;
        this.UnsubscribeInstrument(this.Instrument);
        if (this.Account) {
            this.Account.OnUpdateInfoData.UnSubscribe(this.Account_OnUpdateInfoData, this);
        }
    }
    SubscribeInstrument(instr) {
        if (instr) {
            const qc = DataCache.FQuoteCache;
            qc.addListener(instr, this, HistoryType.QUOTE_LEVEL1);
            qc.addListener(instr, this, HistoryType.QUOTE_TRADES);
        }
    }
    UnsubscribeInstrument(instr) {
        if (instr) {
            const qc = DataCache.FQuoteCache;
            qc.removeListener(instr, this, HistoryType.QUOTE_LEVEL1);
            qc.removeListener(instr, this, HistoryType.QUOTE_TRADES);
        }
    }
    getColumnValue(column) {
        switch (column) {
            case AssetsItem.COL_ACCOUNT:
                return this.Account.FullAccString;
            case AssetsItem.COL_ASSET_NAME:
                return this.Asset.Name;
            case AssetsItem.COL_DEFAULT_SYMBOL:
                if (this.Instrument === null) {
                    return null;
                }
                return this.Instrument.DisplayShortName();
            case AssetsItem.COL_EXCHANGE:
                if (this.Instrument === null) {
                    return null;
                }
                return this.Instrument.TradingExchange;
            case AssetsItem.COL_START_DAY_QTY:
                return this.InfoData.TotalQuantity;
            case AssetsItem.COL_START_DAY_QTY_MARGIN:
                return this.InfoData.TotalQuantityForMarginAvailable;
            case AssetsItem.COL_LIQUIDITY_RATE:
                return this.Asset.LiquidityRate;
            case AssetsItem.COL_TODAY_TRADED_QTY:
                return this.InfoData.TodayTradedQty;
            case AssetsItem.COL_AVAILABLE_QTY_SELL:
                return this.InfoData.GetAvailableQty();
            case AssetsItem.COL_CURRENT_PRICE:
                return this.LastPrice();
            case AssetsItem.COL_CURRENT_VALUE_SELL:
                return this.InfoData.GetCurrentValue(this.InfoData.GetAvailableQty(), this.Account);
            case AssetsItem.COL_CURRENT_QTY:
                return this.InfoData.GetCurrentQty();
            case AssetsItem.COL_CURRENT_VALUE:
                return this.InfoData.GetCurrentValue(this.InfoData.GetCurrentQty(), this.Account);
            case AssetsItem.COL_SELL_EXCHANGE:
                return this.GetSellExchange();
            default:
                return null;
        }
    }
    getColumnData(column) {
        const value = this.getColumnValue(column);
        let formattedValue = '';
        switch (column) {
            case AssetsItem.COL_TODAY_TRADED_QTY:
            case AssetsItem.COL_AVAILABLE_QTY_SELL:
            case AssetsItem.COL_CURRENT_QTY:
                formattedValue = MathUtils.RoundToIncrement(value, !isNullOrUndefined(this.Asset) ? this.Asset.MinChange : 1).toString();
                break;
            case AssetsItem.COL_CURRENT_PRICE:
                if (isNaN(value) || !this.Instrument) {
                    formattedValue = StringUtils.NOT_AVAILABLE;
                }
                else {
                    formattedValue = this.Instrument.formatPrice(value);
                }
                break;
            case AssetsItem.COL_CURRENT_VALUE_SELL:
            case AssetsItem.COL_CURRENT_VALUE:
                if (isNaN(value)) {
                    formattedValue = StringUtils.NOT_AVAILABLE;
                }
                else {
                    formattedValue = this.Account.assetBalanceDefault.Asset.formatPrice(value);
                }
                break;
            default:
                formattedValue = value ? value.toString() : '';
                break;
        }
        return new ColumnData(value, formattedValue);
    }
    GetCurrentInstrument() {
        return this.Instrument;
    }
    getFormatTotalValue(column, value) {
        let formattedValue = '';
        const asset = DataCache.GetAssetByName(DataCache.userBaseCurrency);
        switch (column) {
            case AssetsItem.COL_START_DAY_QTY:
            case AssetsItem.COL_START_DAY_QTY_MARGIN:
            case AssetsItem.COL_TODAY_TRADED_QTY:
            case AssetsItem.COL_AVAILABLE_QTY_SELL:
                formattedValue = value.toFixed(2);
                break;
            case AssetsItem.COL_CURRENT_VALUE_SELL:
                formattedValue = isNullOrUndefined(asset) ? value.toFixed(2) : asset.formatPrice(value);
                break;
            default:
                formattedValue = super.getFormatTotalValue(column, value);
                break;
        }
        return formattedValue;
    }
    addItemListener(listener) {
        const index = this.listeners.indexOf(listener);
        if (index === -1) {
            this.listeners.push(listener);
        }
    }
    removeItemListener(listener) {
        const index = this.listeners.indexOf(listener);
        if (index !== -1) {
            this.listeners.splice(index, 1);
        }
    }
    ColumnCount() {
        return AssetsItem.columnsParams.length;
    }
    GetColumnParams(i) {
        const par = AssetsItem.columnsParams[i];
        par.Hidden = par.DefaultHidden;
        return par;
    }
    GetDataType(columnIndex) {
        switch (columnIndex) {
            case AssetsItem.COL_ACCOUNT:
            case AssetsItem.COL_ASSET_NAME:
            case AssetsItem.COL_DEFAULT_SYMBOL:
            case AssetsItem.COL_SELL_ASSET_BUTTON:
            case AssetsItem.COL_EXCHANGE:
            case AssetsItem.COL_SELL_EXCHANGE:
                return QuickTableComparingType.String;
            case AssetsItem.COL_START_DAY_QTY:
            case AssetsItem.COL_TODAY_TRADED_QTY:
            case AssetsItem.COL_START_DAY_QTY_MARGIN:
            case AssetsItem.COL_LIQUIDITY_RATE:
            case AssetsItem.COL_CURRENT_PRICE:
            case AssetsItem.COL_CURRENT_VALUE_SELL:
            case AssetsItem.COL_CURRENT_VALUE:
            case AssetsItem.COL_AVAILABLE_QTY_SELL:
            case AssetsItem.COL_CURRENT_QTY:
                return QuickTableComparingType.Double;
            default:
                return super.GetDataType(columnIndex);
        }
    }
    getTotalValuePrecision(column, totalCurrencyAfterFiltration) {
        const asset = DataCache.GetAssetByName(DataCache.userBaseCurrency);
        switch (column) {
            case AssetsItem.COL_START_DAY_QTY:
            case AssetsItem.COL_START_DAY_QTY_MARGIN:
            case AssetsItem.COL_TODAY_TRADED_QTY:
            case AssetsItem.COL_AVAILABLE_QTY_SELL:
                return 2;
            case AssetsItem.COL_CURRENT_VALUE_SELL:
                return isNullOrUndefined(asset) ? 2 : asset.Point;
            default:
                return super.getTotalValuePrecision(column, totalCurrencyAfterFiltration);
        }
    }
    GetMenuIndex(headerKey) {
        const index = AssetsItem.ListMenuIndex.indexOf(headerKey);
        return index > -1 ? index : 1000;
        // if (AssetsItem.ListMenuIndex[headerKey]) {
        //     return AssetsItem.ListMenuIndex[headerKey];
        // } else {
        //     return 1000;
        // }
    }
    CalculateQty(input) {
        if (WDSettingsUtils.displayAmountInLots()) {
            return input;
        }
        return input * (this.Instrument ? this.Instrument.LotSize : 1);
    }
}
AssetsItem._staticId = 0;
AssetsItem.COL_ACCOUNT = 0;
AssetsItem.COL_ASSET_NAME = 1;
AssetsItem.COL_DEFAULT_SYMBOL = 2;
AssetsItem.COL_EXCHANGE = 3;
AssetsItem.COL_START_DAY_QTY = 4;
AssetsItem.COL_START_DAY_QTY_MARGIN = 5;
AssetsItem.COL_LIQUIDITY_RATE = 6;
AssetsItem.COL_TODAY_TRADED_QTY = 7;
AssetsItem.COL_AVAILABLE_QTY_SELL = 8;
AssetsItem.COL_CURRENT_PRICE = 9;
AssetsItem.COL_CURRENT_VALUE_SELL = 10;
AssetsItem.COL_SELL_ASSET_BUTTON = 11;
AssetsItem.COL_CURRENT_QTY = 12;
AssetsItem.COL_CURRENT_VALUE = 13;
AssetsItem.COL_SELL_EXCHANGE = 14;
AssetsItem.columnsParams = [
    /* 0 */ new ColumnParams('panel.Assets.QuickTable.Column.Account', 100, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.Assets.QuickTable.Column.AssetName', 100, QTAlignment.Left, TableTotalsVisibility.None, true, false, false, true),
    new ColumnParams('panel.Assets.QuickTable.Column.DefaultSymbol', 100, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.Assets.QuickTable.Column.ExchangeTrading', 100, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.Assets.QuickTable.Column.StartDayQty', 100, QTAlignment.Right, TableTotalsVisibility.All, true, false, false, true),
    /* 5 */ new ColumnParams('panel.Assets.QuickTable.Column.StartDayQtyMargin', 100, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.Assets.QuickTable.Column.LiquidityRate', 100, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.Assets.QuickTable.Column.TodayTradedQty', 100, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.Assets.QuickTable.Column.AvailableQtySell', 100, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.Assets.QuickTable.Column.CurrentPrice', 100, QTAlignment.Right, TableTotalsVisibility.None, true, false, false, false, -1),
    /* 10 */ new ColumnParams('panel.Assets.QuickTable.Column.CurrentValueSell', 100, QTAlignment.Right, TableTotalsVisibility.All, true, false, false, false, -1),
    new ColumnParams('panel.Assets.QuickTable.Column.SellAsset', 100, QTAlignment.Center, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.Assets.QuickTable.Column.CurrentQty', 100, QTAlignment.Left, TableTotalsVisibility.None, true, false, false, false, -1),
    new ColumnParams('panel.Assets.QuickTable.Column.CurrentValue', 100, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.Assets.QuickTable.Column.SellExchange', 100, QTAlignment.Left, TableTotalsVisibility.None, true, false)
];
AssetsItem.ListMenuIndex = [
    'panel.Assets.QuickTable.Column.Account',
    'panel.Assets.QuickTable.Column.AssetName',
    'panel.Assets.QuickTable.Column.DefaultSymbol',
    'panel.Assets.QuickTable.Column.ExchangeTrading',
    'panel.Assets.QuickTable.Column.StartDayQty',
    'panel.Assets.QuickTable.Column.StartDayQtyMargin',
    'panel.Assets.QuickTable.Column.LiquidityRate',
    'panel.Assets.QuickTable.Column.TodayTradedQty',
    'panel.Assets.QuickTable.Column.AvailableQtySell',
    'panel.Assets.QuickTable.Column.CurrentPrice',
    'panel.Assets.QuickTable.Column.CurrentValueSell',
    'panel.Assets.QuickTable.Column.SellAsset',
    'panel.Assets.QuickTable.Column.CurrentQty',
    'panel.Assets.QuickTable.Column.CurrentValue',
    'panel.Assets.QuickTable.Column.SellExchange'
];
